import { DIAMONDS } from "../data/balance";
import type { Diamond, Hazard, Point, VaultId } from "../domain/models";

export interface Particle {
  position: Point;
  velocity: Point;
  life: number;
  maxLife: number;
  color: string;
  size: number;
}

export interface FloatingText {
  position: Point;
  text: string;
  color: string;
  life: number;
  maxLife: number;
}

export class CanvasRenderer {
  readonly context: CanvasRenderingContext2D;
  width = 0;
  height = 0;
  dpr = 1;

  constructor(readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D is unavailable");
    this.context = context;
    this.resize();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = rect.width;
    this.height = rect.height;
    this.canvas.width = Math.round(rect.width * this.dpr);
    this.canvas.height = Math.round(rect.height * this.dpr);
    this.context.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  clear(color = "#040812"): void {
    this.context.fillStyle = color;
    this.context.fillRect(0, 0, this.width, this.height);
  }

  drawBackground(time: number, vault: VaultId): void {
    const ctx = this.context;
    this.clear();
    const palettes: Record<VaultId, [string, string, string]> = {
      street: ["#071827", "#040812", "rgba(70,230,255,.07)"],
      bank: ["#17130e", "#040812", "rgba(255,209,102,.08)"],
      casino: ["#24102c", "#07050d", "rgba(255,86,192,.08)"],
      museum: ["#21170c", "#090805", "rgba(255,178,85,.08)"],
      server: ["#071d19", "#020908", "rgba(74,255,180,.08)"],
      ghost: ["#17112a", "#04030a", "rgba(172,132,255,.09)"],
    };
    const palette = palettes[vault];
    const gradient = ctx.createLinearGradient(0, 0, this.width, this.height);
    gradient.addColorStop(0, palette[0]);
    gradient.addColorStop(1, palette[1]);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.save();
    ctx.strokeStyle = palette[2];
    ctx.lineWidth = 1;
    const spacing = 54;
    const drift = (time * 8) % spacing;
    for (let x = -spacing + drift; x < this.width + spacing; x += spacing) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x - this.height * 0.25, this.height);
      ctx.stroke();
    }
    for (let y = 0; y < this.height; y += spacing) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(this.width, y);
      ctx.stroke();
    }
    ctx.restore();

    this.drawVaultScene(vault, time);
  }

  private drawVaultScene(vault: VaultId, time: number): void {
    const ctx = this.context;
    const w = this.width;
    const h = this.height;
    ctx.save();
    switch (vault) {
      case "street": {
        const count = 9;
        const bw = w / count;
        for (let i = 0; i < count; i += 1) {
          const bx = i * bw;
          const bh = h * (0.08 + ((i * 37) % 11) / 55);
          ctx.fillStyle = "#02060c";
          ctx.fillRect(bx, h - bh, bw * 0.86, bh);
          if (i % 2 === 0) {
            ctx.fillStyle = "rgba(70,230,255,.35)";
            ctx.fillRect(bx + bw * 0.3, h - bh + 8, 4, 4);
          }
        }
        break;
      }
      case "bank": {
        ctx.strokeStyle = "rgba(255,209,102,.12)";
        ctx.lineWidth = 2;
        const cx = w * 0.86;
        const cy = h * 0.5;
        for (let r = 40; r <= 200; r += 40) {
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
        }
        break;
      }
      case "casino": {
        const spot = (x: number, color: string) => {
          const glow = ctx.createRadialGradient(x, -h * 0.1, 10, x, -h * 0.1, h * 0.9);
          glow.addColorStop(0, color);
          glow.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = glow;
          ctx.fillRect(0, 0, w, h);
        };
        spot(w * 0.15, "rgba(255,86,192,.14)");
        spot(w * 0.85, "rgba(120,160,255,.1)");
        break;
      }
      case "museum": {
        ctx.fillStyle = "rgba(255,178,85,.06)";
        const count = 6;
        for (let i = 0; i < count; i += 1) {
          const px = (i + 0.5) * (w / count) - 10;
          ctx.fillRect(px, h * 0.62, 20, h * 0.38);
        }
        break;
      }
      case "server": {
        const rows = 3;
        const cols = 10;
        for (let r = 0; r < rows; r += 1) {
          for (let c = 0; c < cols; c += 1) {
            const rx = c * (w / cols) + 6;
            const ry = h - (r + 1) * 14 - 10;
            ctx.fillStyle = "#02120d";
            ctx.fillRect(rx, ry, w / cols - 8, 10);
            const blink = Math.sin(time * 3 + r * 2 + c) > 0.7;
            ctx.fillStyle = blink ? "rgba(74,255,180,.8)" : "rgba(74,255,180,.15)";
            ctx.fillRect(rx + 4, ry + 4, 3, 3);
          }
        }
        break;
      }
      case "ghost": {
        ctx.shadowColor = "rgba(172,132,255,.6)";
        ctx.shadowBlur = 16;
        ctx.fillStyle = "rgba(172,132,255,.25)";
        for (let i = 0; i < 10; i += 1) {
          const seed = i * 91.7;
          const x = (seed % w + time * 6) % w;
          const y = h - ((time * 14 + seed) % (h + 40));
          ctx.beginPath();
          ctx.arc(x, y, 2 + (i % 3), 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
    }
    ctx.restore();
  }

  drawDiamond(diamond: Diamond): void {
    const cfg = DIAMONDS[diamond.type];
    const ctx = this.context;
    const size = cfg.size * (1 + Math.sin(diamond.pulse) * 0.05);
    ctx.save();
    ctx.translate(diamond.position.x, diamond.position.y);
    ctx.shadowColor = cfg.glow;
    ctx.shadowBlur = 22;

    const body = ctx.createLinearGradient(0, -size, 0, size);
    body.addColorStop(0, "#ffffff");
    body.addColorStop(0.3, cfg.color);
    body.addColorStop(1, cfg.glow);
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.moveTo(0, -size);
    ctx.lineTo(size * 0.78, -size * 0.2);
    ctx.lineTo(size * 0.48, size * 0.72);
    ctx.lineTo(0, size);
    ctx.lineTo(-size * 0.48, size * 0.72);
    ctx.lineTo(-size * 0.78, -size * 0.2);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;

    // Pavilion facet lines beneath the girdle, splitting the lower half into cuts.
    ctx.strokeStyle = "#ffffff3d";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, -size * 0.2);
    ctx.lineTo(0, size);
    ctx.moveTo(-size * 0.48, size * 0.72);
    ctx.lineTo(0, -size * 0.2);
    ctx.moveTo(size * 0.48, size * 0.72);
    ctx.lineTo(0, -size * 0.2);
    ctx.stroke();

    // Crown table highlight with a slow shimmer sweep.
    const shimmer = 0.55 + Math.sin(diamond.pulse * 1.7) * 0.3;
    ctx.strokeStyle = `rgba(255,255,255,${Math.max(0.15, shimmer)})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(-size * 0.78, -size * 0.2);
    ctx.lineTo(size * 0.78, -size * 0.2);
    ctx.lineTo(0, -size);
    ctx.closePath();
    ctx.stroke();

    const specialMarks: Partial<Record<Diamond["type"], string>> = {
      CLEAN_DIAMOND: "C",
      FREEZE_DIAMOND: "F",
      SHIELD_DIAMOND: "S",
      TIME_DIAMOND: "+",
    };
    const mark = specialMarks[diamond.type];
    if (mark) {
      ctx.fillStyle = "#07111f";
      ctx.font = `900 ${Math.max(13, size * 0.72)}px ui-rounded, system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(mark, 0, 1);
    }
    ctx.restore();
  }

  drawPlayer(player: Point, radius: number, danger: boolean, shielded = false): void {
    const ctx = this.context;
    ctx.save();
    ctx.translate(player.x, player.y);
    ctx.shadowColor = danger ? "#ff4d73" : "#46e6ff";
    ctx.shadowBlur = 30;
    ctx.strokeStyle = danger ? "#ff7895" : "#d8fbff";
    ctx.lineWidth = 3;
    ctx.fillStyle = danger ? "#ff4d7333" : "#46e6ff24";
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, 4, 0, Math.PI * 2);
    ctx.fillStyle = "#fff";
    ctx.fill();
    if (shielded) {
      ctx.strokeStyle = "#b8a6ff";
      ctx.shadowColor = "#765cff";
      ctx.shadowBlur = 18;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(0, 0, radius + 8, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  drawHazard(hazard: Hazard, time: number): void {
    const ctx = this.context;
    if (hazard.type === "jammer") {
      ctx.save();
      ctx.translate(hazard.position.x, hazard.position.y);
      ctx.fillStyle = "#9b6cff14";
      ctx.strokeStyle = "#9b6cff88";
      ctx.setLineDash([7, 8]);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(0, 0, hazard.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      return;
    }
    if (hazard.type === "laser" || hazard.type === "hunter") {
      const active = hazard.phase === "active";
      const warning = hazard.phase === "warning";
      if (!active && !warning) return;
      ctx.save();
      ctx.translate(hazard.position.x, hazard.position.y);
      ctx.strokeStyle = active ? "#ff315f" : hazard.type === "hunter" ? "#46e6ff" : "#ffcf66";
      ctx.globalAlpha = active ? 1 : 0.45 + Math.sin(time * 12) * 0.2;
      ctx.shadowColor = ctx.strokeStyle;
      ctx.shadowBlur = active ? 22 : 8;
      ctx.lineWidth = active ? 8 : 2;
      ctx.beginPath();
      const half = (hazard.length ?? 240) / 2;
      if (hazard.orientation === "vertical") {
        ctx.moveTo(0, -half);
        ctx.lineTo(0, half);
      } else {
        ctx.moveTo(-half, 0);
        ctx.lineTo(half, 0);
      }
      ctx.stroke();
      ctx.restore();
      return;
    }
    if (hazard.type === "mine") {
      if (hazard.phase === "cooldown") return;
      const active = hazard.phase === "active";
      const pulse = active ? 1.4 : 1 + Math.sin(time * 9 + hazard.id) * 0.12;
      ctx.save();
      ctx.translate(hazard.position.x, hazard.position.y);
      ctx.scale(pulse, pulse);
      ctx.strokeStyle = active ? "#ff315f" : "#ffb84d";
      ctx.fillStyle = active ? "#ff315f44" : "#ffb84d22";
      ctx.shadowColor = ctx.strokeStyle;
      ctx.shadowBlur = active ? 28 : 12;
      ctx.lineWidth = active ? 5 : 3;
      ctx.beginPath();
      ctx.arc(0, 0, hazard.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      return;
    }
    ctx.save();
    ctx.translate(hazard.position.x, hazard.position.y);
    ctx.rotate(time * 1.8 + hazard.id);
    ctx.strokeStyle = hazard.type === "tracker" ? "#ff9f43" : "#ff4d73";
    ctx.shadowColor = "#ff244f";
    ctx.shadowBlur = 16;
    ctx.lineWidth = 4;
    ctx.strokeRect(-hazard.radius, -hazard.radius, hazard.radius * 2, hazard.radius * 2);
    ctx.restore();
  }

  drawEffects(particles: Particle[], labels: FloatingText[]): void {
    const ctx = this.context;
    for (const particle of particles) {
      const alpha = Math.max(0, particle.life / particle.maxLife);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = particle.color;
      ctx.shadowColor = particle.color;
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(particle.position.x, particle.position.y, particle.size * alpha, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    for (const label of labels) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, label.life / label.maxLife);
      ctx.fillStyle = label.color;
      ctx.textAlign = "center";
      ctx.font = "800 16px ui-rounded, system-ui, sans-serif";
      ctx.shadowColor = "#000";
      ctx.shadowBlur = 8;
      ctx.fillText(label.text, label.position.x, label.position.y);
      ctx.restore();
    }
  }

  drawIdle(time: number): void {
    this.drawBackground(time, "street");
    const ctx = this.context;
    for (let i = 0; i < 16; i += 1) {
      const x = this.width * (0.58 + ((i * 0.173) % 0.42));
      const y = this.height * (((i * 0.287 + time * 0.012) % 1.1) - 0.05);
      ctx.fillStyle = i % 3 === 0 ? "#ffd16622" : "#46e6ff20";
      ctx.beginPath();
      ctx.arc(x, y, 2 + (i % 4), 0, Math.PI * 2);
      ctx.fill();
    }
  }
}
